import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  useNavigate,
  type RouteComponent,
} from "@tanstack/react-router";
import { useEffect, type ComponentType } from "react";
import * as v from "valibot";
import App from "./App";
import { HomeScreen } from "./home-screen";
import { useLocale } from "../i18n";
import { InformationPage } from "./information-page";
import { ApproveScreen } from "../account/approve-screen";
import { ProfileScreen } from "../account/profile-screen";
import { useAccount } from "../data/session";
import { GroupLinkScreen } from "../group-calls/group-link-screen";
import { MessagesScreen } from "../messages/messages-screen";
import { NotificationsScreen } from "../notifications/notifications-screen";
import { FriendsScreen } from "../people/friends-screen";
import { HistoryScreen } from "../people/history-screen";
import type { AuthenticatedSnapshot } from "../../shared/types";

// An unknown language falls away instead of failing the page.
const SearchSchema = v.object({
  lang: v.fallback(v.optional(v.picklist(["ar", "en"])), undefined),
});

function Root() {
  const locale = useLocale(),
    lang = rootRoute.useSearch({ select: (search) => search.lang }),
    navigate = useNavigate();
  // An explicit language link follows the language chosen on the page.
  useEffect(() => {
    if (lang && lang !== locale)
      void navigate({
        to: ".",
        search: (search) => ({ ...search, lang: locale }),
        replace: true,
        resetScroll: false,
      });
  }, [lang, locale, navigate]);
  return <Outlet />;
}
const rootRoute = createRootRoute({
  validateSearch: SearchSchema,
  component: Root,
  notFoundComponent: InformationPage,
});
// The app's shell, around the screen the path chooses.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: App,
});
const view = <Path extends string>(path: Path, component: RouteComponent) =>
  createRoute({ getParentRoute: () => appRoute, path, component });
// Without an account these addresses show the home page. The language is read
// here so a screen re-renders with it.
const accountScreen = (
  Screen: ComponentType<{ account: AuthenticatedSnapshot }>,
) =>
  function AccountScreen() {
    "use no memo";
    useLocale();
    const account = useAccount();
    return account ? <Screen account={account} /> : <HomeScreen />;
  };
// One component for both, so opening a chat keeps the conversation list as is.
const Messages = accountScreen(MessagesScreen);
const page = <Path extends string>(path: Path) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: InformationPage,
  });
// React-admin routes inside /admin itself. Its interface library loads only
// when the panel opens.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "admin",
  component: lazyRouteComponent(
    () => import("../admin/admin-panel"),
    "AdminPanel",
  ),
});
const adminPath = <Path extends string>(path: Path) =>
  createRoute({ getParentRoute: () => adminRoute, path });

export const router = createRouter({
  routeTree: rootRoute.addChildren([
    appRoute.addChildren([
      view("/", HomeScreen),
      view("friends", accountScreen(FriendsScreen)),
      view("history", accountScreen(HistoryScreen)),
      view("messages", Messages),
      view("messages/$personId", Messages),
      view("notifications", accountScreen(NotificationsScreen)),
      view("profile", accountScreen(ProfileScreen)),
      view("call/$link", HomeScreen),
      view("group/$groupCode", GroupLinkScreen),
      // A shared profile link opens its owner's profile.
      view("add/$code", HomeScreen),
      // Opening it changes nothing until the person approves.
      view("approve/$signInCode", ApproveScreen),
    ]),
    page("privacy"),
    page("terms"),
    adminRoute.addChildren([adminPath("/"), adminPath("$")]),
  ]),
});
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
